import type { ComponentProps, FC, ReactNode } from "react";
import type { CheckboxGroup } from "~/components/ui/checkbox";
import { Input, type Input as InputType } from "~/components/ui/input";
import { Label } from "~/components/ui/label";
import type { Select } from "~/components/ui/select";
import type { Button } from "react-day-picker";

export type FormComponentType =
  | React.ForwardRefExoticComponent<ComponentProps<typeof InputType>>
  | React.ForwardRefExoticComponent<ComponentProps<typeof Select>>
  | (() => JSX.Element)
  | React.ForwardRefExoticComponent<
      ComponentProps<typeof Button> & React.RefAttributes<HTMLButtonElement>
    >
  | React.ForwardRefExoticComponent<ComponentProps<typeof CheckboxGroup>>;

export type FormFieldType = {
  id: string;
  label: string;
  type:
    | "text"
    | "number"
    | "password"
    | "email"
    | "datetime-local"
    | "date"
    | "time"
    | "select"
    | "button"
    | "submit"
    | "custom"
    | "checkbox-group"
    | "hidden";
  name: string;
  required?: boolean;
  Component: FormComponentType;
  screenReaderDescription: string;
  error?: string;
  placeholder?: string;
  defaultValue?: string;
  disabled?: boolean;
  readOnly?: boolean;
  options?: { value: string; label: string }[];
  checkboxOptions?: {
    value: string;
    label: string;
    defaultChecked?: boolean;
  }[];
  className?: string;
  containerClassName?: string;
  children?: ReactNode;
  maxLength?: number;
  onChange?: (value: string) => void;
};

interface FormBuilderProps {
  fields: FormFieldType[];
  className?: string;
  inline?: boolean;
  children?: any;
}

const FormBuilder: FC<FormBuilderProps> = ({
  fields,
  inline = false,
  children,
}) => {
  return (
    <div className={inline ? "flex items-end gap-4" : "space-y-4"}>
      {fields.map((field: FormFieldType) => {
        const commonProps = {
          id: field.id,
          name: field.name,
          disabled: field.disabled,
          readOnly: field.readOnly,
          required: field.required,
          placeholder: field.placeholder,
          defaultValue: field.defaultValue,
          "aria-required": field.required,
          "aria-describedby": `${field.id}-description`,
          className: field.className,
          children: field.children,
        };

        return (
          <div
            key={field.name}
            className={`${field.containerClassName || ""} mb-4`}
          >
            {field.label && (
              <Label htmlFor={field.id}>
                {field.label}
                {field.required && "*"}
              </Label>
            )}

            {field.type === "button" || field.type === "submit" ? (
              <field.Component {...commonProps} type={field.type} options={[]}>
                {field.children && field.children}
              </field.Component>
            ) : field.type === "select" ? (
              <field.Component
                {...commonProps}
                onChange={(e) => {
                  if (e.target instanceof HTMLSelectElement && field.onChange) {
                    field.onChange(e.target.value);
                  }
                }}
                options={field.options || []}
              />
            ) : field.type === "checkbox-group" ? (
              <field.Component
                {...commonProps}
                options={field.checkboxOptions || []}
              />
            ) : field.type === "custom" ? (
              <field.Component {...commonProps} options={field.options || []} />
            ) : (
              <Input
                type={field.type}
                {...commonProps}
                maxLength={field.maxLength}
              />
            )}

            <span id={`${field.id}-description`} className="sr-only">
              {field.screenReaderDescription}
            </span>
            {field.error && (
              <p className="text-sm text-red-500">{field.error}</p>
            )}
          </div>
        );
      })}
      {children}
    </div>
  );
};

export default FormBuilder;
